export enum CoreAssetsEventNames {
  CopyAssets = 'core-assets.copy-assets-client',
  CreateVersionStack = 'core-assets.create-version-stack-client',
  DeleteAssets = 'core-assets.delete-assets-client',
  GetFinderItems = 'core-assets.get-finder-items-client',
  MoveAssets = 'core-assets.move-assets-client',
  UploadedAssets = 'core-assets.assets-uploaded-client',
  CreateProject = 'core-assets.create-project-client',
  CreateFolder = 'core-assets.create-folder-client',
}

/*
 * All analytics events should follow recommendations from Data team, detailed here:
 * https://frame-io.slack.com/archives/C02NE1JV3KM/p1666037102832429
 */
export type CoreAssetsEvents = UploadEvent;

/*
 * Upload Events
 */
export type UploadEvent =
  | UploadMaxFilesExceeded
  | UploadMaxFoldersExceeded
  | UploadStarted
  | UploadPaused
  | UploadResumed
  | UploadCanceled
  | UploadCompleted
  | UploadRemoved
  | UploadFailed
  | UploadTranscodeComplete
  | UploadMaxConcurrentBatchExceeded
  | UploadMaxConcurrentFileLimitExceeded
  | UploadMaxFolderDepthExceeded;

export type UploadDetails = {
  id: string;
  upload_type?: string;
  project_id?: string;
  started_at: string;
  job_duration_milliseconds: number;
  created_folders_count?: number;
  failed_files_count?: number;
  viewable_files_count?: number;
  files_count?: number;
  root_files_count?: number;
  root_folders_count?: number;
  total_bytes: number;
  bytes_sent: number;
};

export type UploadErrorDetails = {
  id: string;
  project_id: string;
  files_count: number;
};

export type UploadMaxFilesExceeded = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'max-files-exceeded';
  details: {
    id: string;
    upload_type: string;
    project_id: string | undefined;
    file_count: number;
    max_file_count: number;
  };
};

export type UploadMaxFoldersExceeded = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'max-folders-exceeded';
  details: {
    id: string;
    upload_type: string;
    project_id: string | undefined;
    folder_count: number;
    max_folder_count: number;
  };
};

export type UploadMaxFolderDepthExceeded = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'max-folder-depth-exceeded';
  details: {
    id: string;
    upload_type: string;
    project_id: string | undefined;
    max_folder_depth_limit: number;
  };
};

export type UploadMaxConcurrentBatchExceeded = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'max-concurrent-batch-exceeded';
  details: {
    id: string;
    upload_type: string;
    project_id: string;
    max_concurrent_batch_limit: number;
  };
};

export type UploadMaxConcurrentFileLimitExceeded = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'max-concurrent-file-limit-exceeded';
  details: {
    id: string;
    upload_type: string;
    project_id: string;
    max_concurrent_file_limit: number;
  };
};

export type UploadStarted = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'started';
  details: UploadDetails;
};
export type UploadPaused = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'paused';
  details: UploadDetails;
};
export type UploadResumed = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'resumed';
  details: UploadDetails;
};
export type UploadCanceled = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'canceled';
  details: UploadDetails;
};
export type UploadCompleted = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'completed';
  details: UploadDetails;
};
export type UploadRemoved = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'removed';
  details: UploadDetails;
};
export type UploadFailed = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'failed';
  details: UploadDetails;
};
export type UploadTranscodeComplete = {
  name: CoreAssetsEventNames.UploadedAssets;
  status: 'transcodes-complete';
  details: UploadDetails;
};
